import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Mic, Loader2, Square } from 'lucide-react';
import { api } from '@/lib/api';
import { navigateToSettingsTab } from '@/lib/settings-deep-link';
import type { ApiResponse } from '@/types';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export type VoicePlacement = 'inline' | 'textarea';

function getSpeechRecognitionCtor(): (new () => SpeechRecognition) | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & { webkitSpeechRecognition?: new () => SpeechRecognition };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function browserSupportsWebSpeech(): boolean {
  return Boolean(getSpeechRecognitionCtor());
}

function pickRecorderMime(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}

function appendTranscript(current: string, addition: string): string {
  const add = addition.trim();
  if (!add) return current;
  if (!current.trim()) return add;
  const last = current.slice(-1);
  if (/\s/.test(last) || last === '' || last === '\n') return current + add;
  return `${current} ${add}`;
}

export interface VoiceInputButtonProps {
  value: string;
  onValueChange: (value: string) => void;
  /** When the field is uncontrolled, pass the input/textarea ref so the current DOM value is used. */
  inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  disabled?: boolean;
  placement?: VoicePlacement;
  /** Mic sits left of the KI button when true (AITextarea / AIInput). */
  pairedWithAi?: boolean;
  /** BCP-47, e.g. de-DE */
  lang?: string;
  className?: string;
}

/**
 * Voice dictation: Web Speech API in Chromium-based browsers; MediaRecorder + OpenAI Whisper on the API for others (e.g. Firefox).
 */
export function VoiceInputButton({
  value,
  onValueChange,
  inputRef,
  disabled,
  placement = 'textarea',
  pairedWithAi = false,
  lang = 'de-DE',
  className,
}: VoiceInputButtonProps) {
  const [mode, setMode] = useState<'idle' | 'web' | 'record' | 'upload'>('idle');
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const baseTextRef = useRef('');
  const accumulatedRef = useRef('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const mimeRef = useRef('');

  const stopWeb = useCallback(() => {
    const r = recognitionRef.current;
    recognitionRef.current = null;
    try {
      r?.stop();
    } catch {
      /* ignore */
    }
    setMode('idle');
  }, []);

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    try {
      if (mr && mr.state !== 'inactive') mr.stop();
    } catch {
      /* ignore */
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setMode('idle');
  }, []);

  useEffect(() => {
    return () => {
      stopWeb();
      stopRecording();
    };
  }, [stopWeb, stopRecording]);

  const currentFieldText = useCallback(() => inputRef?.current?.value ?? value, [inputRef, value]);

  const startWebSpeech = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      toast.error('Spracherkennung wird in diesem Browser nicht unterstützt.');
      return;
    }
    baseTextRef.current = currentFieldText();
    accumulatedRef.current = '';
    const rec = new Ctor();
    recognitionRef.current = rec;
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const row = event.results[i];
        if (!row) continue;
        const piece = row[0]?.transcript ?? '';
        if (row.isFinal) {
          accumulatedRef.current += piece;
        } else {
          interim += piece;
        }
      }
      const merged = baseTextRef.current + accumulatedRef.current + interim;
      onValueChange(merged);
    };
    rec.onerror = (e: SpeechRecognitionErrorEvent) => {
      if (e.error === 'aborted' || e.error === 'no-speech') return;
      toast.error(e.message || `Spracherkennung: ${e.error}`);
      stopWeb();
    };
    rec.onend = () => {
      if (recognitionRef.current === rec) {
        recognitionRef.current = null;
        setMode('idle');
      }
    };
    try {
      rec.start();
      setMode('web');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Spracherkennung konnte nicht gestartet werden.');
      recognitionRef.current = null;
    }
  }, [currentFieldText, lang, onValueChange, stopWeb]);

  const uploadBlob = useCallback(
    async (blob: Blob) => {
      setMode('upload');
      const fd = new FormData();
      const ext = blob.type.includes('mp4') ? 'm4a' : 'webm';
      fd.append('file', blob, `dictation.${ext}`);
      fd.append('language', lang.slice(0, 2) || 'de');
      try {
        const res = await api.upload<ApiResponse<{ text: string }>>('/ai/transcribe', fd, { timeoutMs: 120000 });
        if (!res.success) {
          if (res.error_code === 'whisper_no_credentials') {
            toast.error(res.error || 'OpenAI API-Schlüssel für Spracherkennung fehlt.', {
              duration: 14_000,
              action: {
                label: 'Zu den Credentials',
                onClick: () => navigateToSettingsTab('providers'),
              },
            });
            return;
          }
          throw new Error(res.error || 'Transkription fehlgeschlagen');
        }
        const base = inputRef?.current?.value ?? value;
        const textOut = (res.data?.text ?? '').trim();
        if (!textOut) {
          toast.message('Kein Sprachtext erkannt — bitte erneut sprechen oder lauter.');
          return;
        }
        onValueChange(appendTranscript(base, textOut));
        toast.success('Text übernommen');
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Transkription fehlgeschlagen');
      } finally {
        setMode('idle');
      }
    },
    [inputRef, value, lang, onValueChange]
  );

  const startFallbackRecord = useCallback(async () => {
    const mime = pickRecorderMime();
    if (!mime || typeof MediaRecorder === 'undefined') {
      toast.error('Audioaufnahme wird in diesem Browser nicht unterstützt.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      mimeRef.current = mime;
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeRef.current || 'audio/webm' });
        chunksRef.current = [];
        if (blob.size > 0) void uploadBlob(blob);
        else setMode('idle');
      };
      mr.start(250);
      setMode('record');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Mikrofonzugriff verweigert');
    }
  }, [uploadBlob]);

  const toggle = () => {
    if (disabled || mode === 'upload') return;
    if (mode === 'web') {
      stopWeb();
      return;
    }
    if (mode === 'record') {
      stopRecording();
      return;
    }
    if (browserSupportsWebSpeech()) {
      startWebSpeech();
    } else {
      void startFallbackRecord();
    }
  };

  const busy = mode === 'upload';
  const active = mode === 'web' || mode === 'record';

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled || busy}
      title={
        browserSupportsWebSpeech()
          ? active
            ? 'Diktat beenden'
            : 'Diktat (Browser)'
          : active
            ? 'Aufnahme beenden'
            : 'Diktat (Server-Transkription)'
      }
      className={cn(
        'pointer-events-auto inline-flex items-center justify-center rounded-md transition-colors',
        'text-muted hover:text-success hover:bg-success-soft',
        placement === 'textarea'
          ? pairedWithAi
            ? 'absolute bottom-2 right-10 z-10 h-7 w-7 text-sm'
            : 'absolute bottom-2 right-1.5 z-10 h-7 w-7 text-sm'
          : pairedWithAi
            ? 'absolute right-9 top-1/2 z-10 h-7 w-7 -translate-y-1/2 text-sm'
            : 'absolute right-1 top-1/2 z-10 h-7 w-7 -translate-y-1/2 text-sm',
        (disabled || busy) && 'cursor-not-allowed opacity-50',
        active && 'text-success ring-2 ring-success/30',
        className
      )}
      aria-pressed={active}
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : active ? (
        <Square className="h-3 w-3 fill-current" />
      ) : (
        <Mic className="h-3.5 w-3.5" />
      )}
    </button>
  );
}
